import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useForm, SubmitHandler } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import useUser from "@/hooks/useUser";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  Select,
  SelectItem,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useUseCases } from "@/context/UseCasesProvider";
import { archiveReasons } from "../dictionaries/archiveReasons";
import archiveModalImg from "../../../archive-modal.png";

type ArchiveModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  redirect?: boolean;
};

type FormValues = {
  reason: string;
  motif: string;
};

interface UserData {
  user_id: string;
  [key: string]: any;
}

const ArchiveModal = ({ isOpen, onClose, orderId, redirect = false }: ArchiveModalProps) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const settingsInterface = useUseCases();

  const [selectedReason, setSelectedReason] = useState<string>("hand-processed");
  const { register, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      reason: "hand-processed",
      motif: "",
    },
  });

  const { data: userData } = useUser();

  const currentUserUUID = userData?.user_id;

  const onSubmit: SubmitHandler<FormValues> = async (data, event) => {
    event?.preventDefault(); 
    event?.stopPropagation(); // avoid submit other form on the same page

    try {
      const response = await axiosPrivate.get(`/orders/${orderId}`);
      let orderData = response.data;
      orderData.process.status = "archive";

      if (!orderData.process.orderInteractionHistory.archive) {
        orderData.process.orderInteractionHistory.archive = {};
      }

      if (currentUserUUID) {
        orderData.process.orderInteractionHistory.archive[currentUserUUID] = [
          {
            datetime: new Date().toISOString(),
            reason: data.reason,
            comment: data.motif,
          },
        ];
      } else {
        console.error("currentUserUUID is undefined");
      }

      await axiosPrivate.put(`/orders/${orderId}`, orderData);

      queryClient.invalidateQueries({ queryKey: ["orders"] });
      
      if (redirect) {
        navigate('/orders');
      } else {
        onClose();
      }
    } catch (error) {
      console.error(t("error_archiving"), error);
    }
  };

  const handleSelectChange = (value: string) => {
    if (value !== selectedReason && value) {
      setSelectedReason(value);
      setValue("reason", value);
    }
  };
  watch("reason");

  const archiveCustomMessage = settingsInterface?.settings?.interface?.custom_message?.archive;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop:
          "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <div className="flex pt-5">
            <div>
              <div className="text-2xl font-bold pt-3 pb-6">
                {t("archive_order")}
              </div>
              <div className="text-vistagreen">
                {t("order_not_valid_prompt")}
              </div>
            </div>
            <img
              src={archiveModalImg}
              alt={t("vistaflow_archive")}
              className="w-48 origin-left self-center"
            />
          </div>
          
          {archiveCustomMessage?.trim() && (
            <div className="text-center p-2 rounded-2xl border-medium border-dashed border-red-400 text-red-600 font-bold">{archiveCustomMessage}</div>
          )}

          <div className="font-bold">{t("archive_reason")}:</div>
          <Select
            placeholder={t("select")}
            defaultSelectedKeys={["hand-processed"]}
            disabledKeys={[selectedReason]}
            labelPlacement="outside"
            className="max-w-lg"
            disableSelectorIconRotation
            disallowEmptySelection
            onChange={(e) => handleSelectChange(e.target.value)}
          >
            {archiveReasons.map((archiveReason) => (
              <SelectItem 
                key={archiveReason.value} 
                value={archiveReason.value}
              >
                {t(archiveReason.label)}
              </SelectItem>
            ))}
          </Select>
          <div className="font-bold pt-5">{t("comment")}</div>
          <Textarea
            minRows={10}
            placeholder={t("specify_reason_or_reference")}
            {...register("motif")}
          >
            Ici
          </Textarea>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button type="submit" className="bg-vistagreen text-white">
            {t("confirm_archiving")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ArchiveModal;